<dialog
  #dialog
  class="external-brief-link-dialog m-auto max-h-screen overflow-y-auto rounded-xl border border-border bg-card p-0 text-foreground shadow-xl backdrop:bg-black/50"
  aria-labelledby="external-brief-link-title"
  (click)="onBackdropClick($event)"
>
  <div class="flex flex-col gap-4 p-6">
    <h2 id="external-brief-link-title" class="m-0 text-xl font-bold tracking-tight text-foreground">Want to upload a document?</h2>
    <p class="m-0 text-sm leading-relaxed text-muted-foreground">
      The overview has to be the brief itself. If that content lives in a separate file — a PDF, DOCX, PPTX, or Google
      Doc — choose Document, then drop the file in or paste a public Google Doc link.
    </p>

    <div class="demo-stage relative h-96 overflow-hidden rounded-lg border border-border bg-muted/40 p-3" aria-hidden="true">
      <div class="demo-chooser absolute inset-3 flex flex-col gap-2">
        <p class="m-0 text-sm font-bold text-foreground">How do you want to define your creative direction?</p>
        <ul class="grid grid-cols-2 gap-2">
          @for (method of methods; track method.id) {
          <li [class]="cardClass(method.id)">
            @if (method.tag) {
            <span class="absolute right-2 top-2 rounded-full bg-brand-studio px-2 py-0.5 text-xs font-extrabold tracking-wider text-white">{{ method.tag }}</span>
            }
            <span [class]="iconClass(method.id)">
              @switch (method.id) {
              @case ('upload') {
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                <path d="M12 13v8" />
                <path d="M4 14.899A7 7 0 1 1 15.71 8h1.79a4.5 4.5 0 0 1 2.5 8.242" />
                <path d="m8 17 4-4 4 4" />
              </svg>
              }
              @case ('type') {
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                <path d="M12 20h9" />
                <path d="M16.376 3.622a1 1 0 0 1 3.002 3.002L7.368 18.635a2 2 0 0 1-.855.506l-2.872.838a.5.5 0 0 1-.62-.62l.838-2.872a2 2 0 0 1 .506-.854z" />
              </svg>
              }
              @case ('template') {
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                <rect width="18" height="7" x="3" y="3" rx="1" />
                <rect width="9" height="7" x="3" y="14" rx="1" />
                <rect width="5" height="7" x="16" y="14" rx="1" />
              </svg>
              }
              @case ('ai') {
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                <path d="M9.937 15.5A2 2 0 0 0 8.5 14.063l-6.135-1.582a.5.5 0 0 1 0-.962L8.5 9.936A2 2 0 0 0 9.937 8.5l1.582-6.135a.5.5 0 0 1 .963 0L14.063 8.5A2 2 0 0 0 15.5 9.937l6.135 1.581a.5.5 0 0 1 0 .964L15.5 14.063a2 2 0 0 0-1.437 1.437l-1.582 6.135a.5.5 0 0 1-.963 0z" />
              </svg>
              }
              }
            </span>
            <p class="m-0 text-sm font-bold text-foreground">{{ method.title }}</p>
            <p class="m-0 text-xs leading-relaxed text-muted-foreground">{{ method.desc }}</p>
          </li>
          }
        </ul>
        <svg class="demo-cursor size-6 text-foreground" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" aria-hidden="true">
          <path fill="currentColor" d="M5.5 3.21V20.8c0 .45.54.67.85.35l4.86-4.86a.5.5 0 0 1 .35-.15h6.87a.5.5 0 0 0 .35-.85L6.35 2.86a.5.5 0 0 0-.85.35z" />
        </svg>
      </div>

      <div class="demo-drop absolute inset-3 flex flex-col gap-2">
        <div class="demo-dropzone relative flex min-h-28 flex-col items-center justify-center rounded-lg border border-dashed border-border px-4 py-3 text-center">
          <div class="demo-file absolute top-2 flex flex-col items-center gap-1 rounded-md border border-border bg-card px-3 py-1.5 shadow-sm">
            <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="text-foreground" aria-hidden="true">
              <path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z" />
              <path d="M14 2v4a2 2 0 0 0 2 2h4" />
              <path d="M10 9H8" />
              <path d="M16 13H8" />
              <path d="M16 17H8" />
            </svg>
            <p class="m-0 text-xs font-semibold text-foreground">brief.pdf</p>
          </div>
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="size-6 text-muted-foreground" aria-hidden="true">
            <path d="M11.35 22H6a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h8a2.4 2.4 0 0 1 1.706.706l3.588 3.588A2.4 2.4 0 0 1 20 8v5.35" />
            <path d="M14 2v5a1 1 0 0 0 1 1h5" />
            <path d="M14 19h6" />
            <path d="M17 16v6" />
          </svg>
          <p class="m-0 mt-2 text-sm font-semibold text-foreground">Upload a Document</p>
          <p class="m-0 text-xs text-muted-foreground">or drag and drop</p>
        </div>
        <p class="m-0 text-center text-xs font-semibold text-muted-foreground">or</p>
        <div class="flex flex-col gap-1">
          <p class="m-0 text-sm font-semibold text-foreground">Public Google Doc link</p>
          <div class="demo-link-input flex h-9 items-center overflow-hidden rounded-md border border-border bg-card px-3">
            <p class="demo-link-value m-0 overflow-hidden text-sm whitespace-nowrap text-foreground">https://docs.google.com/document/d/…</p>
            <span class="demo-caret"></span>
          </div>
          <p class="m-0 text-xs text-muted-foreground">Share the doc so anyone with the link can view it.</p>
        </div>
      </div>
    </div>

    <div class="flex flex-wrap justify-end gap-2">
      <button type="button" class="btn-shout btn-shout-sm btn-shout-secondary shrink-0" (click)="close()">
        I'll write it here
      </button>
      <button type="button" class="btn-shout btn-shout-sm btn-shout-primary shrink-0" (click)="chooseDocument()">
        Use Document
      </button>
    </div>
  </div>
</dialog>
